import type { ProductItem, ProductData } from './types';

function normalizeProductIdForKey(raw: unknown): number | undefined {
  if (raw === undefined || raw === null || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  if (Number.isNaN(n) || n <= 0) return undefined;
  return n;
}

/**
 * Alinea paso 1 y unidades guardadas aunque `productCode` difiera (API vs formulario).
 * Prioriza productId para evitar falsos "reducción" al subir cantidad (ej. 2→3).
 * Nunca usa nombre: los nombres de catálogo no son únicos.
 */
export function correlationKeyFromItem(item: ProductItem): string | null {
  const pid = normalizeProductIdForKey((item as { productId?: unknown }).productId);
  if (pid != null) return `id:${pid}`;
  const code = (item.productCode ?? '').trim();
  if (code) return `code:${code}`;
  return null;
}

export function correlationKeyFromUnit(u: {
  productId?: number | string;
  productCode?: string;
}): string | null {
  const rawId = (u as { productId?: unknown }).productId;
  const pid = typeof rawId === 'number' ? rawId : parseInt(String(rawId ?? ''), 10);
  if (!Number.isNaN(pid) && pid > 0) return `id:${pid}`;
  const code = (u.productCode ?? '').trim();
  if (code) return `code:${code}`;
  return null;
}

/**
 * Mantiene unidades cuyo producto sigue en el paso 1 (misma clave id o código).
 * Filtro de seguridad: con el modelo unificado, productItems se deriva siempre de
 * productUnits, así que en el flujo normal esto nunca quita nada; sirve de defensa
 * ante datos legados (borradores creados con el flujo anterior).
 */
export function filterProductUnitsToProductItems(
  units: ReadonlyArray<{
    productCode?: string;
    productId?: number | string;
    productName?: string;
  }>,
  items: ProductItem[],
): any[] {
  const itemKeys = new Set<string>();
  for (const item of items) {
    const ikey = correlationKeyFromItem(item);
    if (ikey) itemKeys.add(ikey);
  }
  if (itemKeys.size === 0) return [];

  return units.filter((u) => {
    const ukey = correlationKeyFromUnit(u);
    return ukey != null && itemKeys.has(ukey);
  });
}

/** Catalog scalar → form string; empty when missing (never guess). */
export function catalogFieldToString(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string' && value.trim() === '') return '';
  return String(value);
}

/** API `lengthMm` (mm) → texto del formulario “Largo (cm)”. */
export function lengthMmToCmField(lengthMm: string | number | undefined | null): string {
  if (lengthMm == null) return '';
  if (typeof lengthMm === 'string' && lengthMm.trim() === '') return '';
  const n = Number(lengthMm);
  if (!Number.isFinite(n)) return '';
  return String(n / 10);
}

function normalizeProductId(raw: unknown): number | undefined {
  return normalizeProductIdForKey(raw);
}

/**
 * Resuelve ficha de catálogo solo por identidad estable.
 * Orden: productId → code (único). Nunca por nombre (nombres duplicados en joyería).
 * Si no está en el cache local, retorna undefined (defaults vacíos; no adivinar).
 */
export function findCatalogProduct(
  item: ProductItem,
  products: ProductData[],
): ProductData | undefined {
  const nid = normalizeProductId((item as { productId?: unknown }).productId);
  if (nid != null) {
    const byId = products.find((p) => p.id === nid);
    if (byId) return byId;
  }
  const code = (item.productCode ?? '').trim();
  if (code) {
    return products.find((p) => p.code === code);
  }
  return undefined;
}

/** Unidad compatible con el estado de la sección de piezas (una fila por pieza). */
export type MergedProductUnit = {
  id: string;
  productId: number;
  productName: string;
  productCode: string;
  quantity: number;
  properties: {
    supplierId?: string;
    materialId: string;
    materialName: string;
    weight: string;
    length: string;
    diameter: string;
    color: string;
    workshopCode: string;
  };
  costs: { costPrice: string; salePrice: string };
};

/**
 * Crea `quantity` piezas nuevas para un producto, con valores por defecto del catálogo
 * (o vacíos si no está en cache local — nunca se adivina). Usado tanto al agregar un
 * producto nuevo a la lista como al subir la cantidad de una sección existente.
 */
export function createDefaultUnitsForProduct(params: {
  productId: number;
  productName: string;
  productCode: string;
  quantity: number;
  product: ProductData | undefined;
  selectedSupplierId: string | undefined;
}): MergedProductUnit[] {
  const { productId, productName, productCode, quantity, product, selectedSupplierId } = params;
  const units: MergedProductUnit[] = [];
  for (let i = 0; i < quantity; i++) {
    units.push({
      id: `${productCode !== '' ? productCode : 'p'}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${i}`,
      productId,
      productName,
      productCode,
      quantity: 1,
      properties: {
        supplierId: selectedSupplierId ?? '',
        materialId: product?.material?.id != null ? product.material.id.toString() : '',
        materialName: product?.material?.name ?? '',
        weight: catalogFieldToString(product?.weightGrams),
        length: lengthMmToCmField(product?.lengthMm),
        diameter: catalogFieldToString(product?.diameterSize),
        color: catalogFieldToString(product?.color),
        workshopCode: catalogFieldToString(product?.workshopCode),
      },
      costs: {
        costPrice: catalogFieldToString(product?.costPrice),
        salePrice: catalogFieldToString(product?.salePrice),
      },
    });
  }
  return units;
}

/**
 * Reemplaza las piezas de un producto conservando su posición en la lista (la sección no
 * salta al final al cambiar su cantidad). Si el producto no tenía piezas, las agrega al final.
 */
export function replaceSectionUnitsInPlace<T extends { productId: number }>(
  units: ReadonlyArray<T>,
  productId: number,
  nextSection: ReadonlyArray<T>,
): T[] {
  const firstIndex = units.findIndex((u) => u.productId === productId);
  const others = units.filter((u) => u.productId !== productId);
  if (firstIndex === -1) return [...others, ...nextSection];
  const insertAt = units.slice(0, firstIndex).filter((u) => u.productId !== productId).length;
  return [...others.slice(0, insertAt), ...nextSection, ...others.slice(insertAt)];
}

/** Orden de los productos según la primera aparición de sus piezas (así se ven las secciones). */
export function productOrderFromUnits(units: ReadonlyArray<{ productId: number }>): number[] {
  const order: number[] = [];
  const seen = new Set<number>();
  for (const unit of units) {
    if (!seen.has(unit.productId)) {
      seen.add(unit.productId);
      order.push(unit.productId);
    }
  }
  return order;
}

/**
 * Mueve un producto completo (todas sus piezas) un lugar arriba (-1) o abajo (+1).
 * Devuelve el mismo array si no se puede mover (primero/último o producto inexistente).
 */
export function moveSectionUnits<T extends { productId: number }>(
  units: T[],
  productId: number,
  direction: -1 | 1,
): T[] {
  // One pass: sections keyed by product, in order of first appearance (Map keeps insertion order).
  const sections = new Map<number, T[]>();
  for (const unit of units) {
    const section = sections.get(unit.productId);
    if (section == null) {
      sections.set(unit.productId, [unit]);
    } else {
      section.push(unit);
    }
  }
  const order = [...sections.keys()];
  const from = order.indexOf(productId);
  const to = from + direction;
  if (from === -1 || to < 0 || to >= order.length) return units;
  [order[from], order[to]] = [order[to], order[from]];
  return order.flatMap((id) => sections.get(id) ?? []);
}

/** Ordena las filas del paso 1 según el orden de productos dado; las que no estén quedan al final. */
export function sortItemsByProductOrder(
  items: ProductItem[],
  productOrder: number[],
): ProductItem[] {
  const position = new Map(productOrder.map((id, index) => [id, index]));
  const rank = (item: ProductItem): number => {
    const pid = normalizeProductIdForKey(item.productId);
    return (pid != null ? position.get(pid) : undefined) ?? Number.MAX_SAFE_INTEGER;
  };
  return [...items].sort((a, b) => rank(a) - rank(b));
}
